'use client';

import { clsx } from 'clsx';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'atoms/Button';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { ParseBBCode } from 'atoms/ParseBBCode';
import { P, Span } from 'atoms/Typography';
import { VariantItem } from 'atoms/VariantLink';
import { DeliveryStatus, DeliveryStatusType } from 'molecules/DeliveryStatus';
import { ProductReferencePrice } from 'molecules/ProductReferencePrice';
import { ProductSize } from 'molecules/ProductSize';
import { ReviewsRating } from 'molecules/ReviewsRating';
import { Vat } from 'molecules/Vat';
import {
  PriceInfo,
  ProductQuantitySelect,
  SizeSelect,
} from 'organisms/ProductPriceSection';
import {
  ProductDiscount,
  ProductPrice,
} from 'organisms/ProductPriceSection/PriceInfo';
import { useProductSizeSelect } from 'organisms/ProductPriceSection/useProductSizeSelect';
import { ProductMaterialInfo } from 'pages/ProductDetail/ProductMaterialInfo';
import { ProductSizesProvider } from 'pages/ProductDetail/context';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { useRouter } from 'modules/next/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import {
  useValidationRules,
  useYupValidationResolver,
} from 'modules/validation/hooks';
import {
  IAdaptedBasicProduct,
  IAdaptedProduct,
} from 'commerce/scayle/utils/adapter/types';
import styles from './styles.module.css';

export const ListicleTile: FC<{
  bapiProduct: IAdaptedProduct;
  index: number;
}> = ({ bapiProduct: parsedProduct, index }) => {
  const t = useTranslations();
  const { isMobile, isTablet } = useMediaQuery();
  const { gtmData } = useSiteConfig();
  const { push } = useRouter();

  const [state, setState] = useState({
    quantity: 1,
    listicleProductRefKey: parsedProduct.referenceKey,
  });

  const siblings = parsedProduct.siblings || [];
  const productWithSiblingsByRefKey: Record<string, IAdaptedBasicProduct> = {
    [parsedProduct.referenceKey]: parsedProduct,
  };

  siblings.forEach(
    (sibling) => (productWithSiblingsByRefKey[sibling.referenceKey] = sibling),
  );

  const product = productWithSiblingsByRefKey[state.listicleProductRefKey];
  const { addToCart } = useCart();

  const {
    hasSizes,
    sizeOptions,
    selectedVariant,
    quantityLimit,
    setSelectedVariant,
  } = useProductSizeSelect(product, product.referenceKey, true);

  const { size } = useValidationRules();
  const resolver = useYupValidationResolver({
    size,
  });
  const { control, trigger, watch } = useForm({
    resolver,
    values: { size: selectedVariant?.toString() },
    mode: 'onChange',
  });

  const formSizeValue = watch('size');

  useEffect(() => {
    const size = String(formSizeValue);

    setSelectedVariant(size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSizeValue]);

  const activeVariant =
    product.variants?.find(
      (variant) => variant.referenceKey === selectedVariant,
    ) || product.variants?.[0];

  const isOutOfStock =
    !activeVariant?.stock || activeVariant.stock.quantity === 0;
  const isDisabled = isOutOfStock || (hasSizes && !selectedVariant);

  const sortedVariants = Object.values(productWithSiblingsByRefKey)
    .sort((a, b) => a.href.localeCompare(b.href))
    .filter((variant) => !variant.isSoldOut);

  const transformedSizeOptions = sizeOptions.map((option) => ({
    ...option,
    label:
      option.disabled && option.value
        ? t('product.pdp.soldOut', { label: option.label })
        : option.label,
  }));

  const handleClick = async () => {
    if (!activeVariant?.referenceKey) {
      return;
    }

    if (hasSizes && !selectedVariant) {
      trigger();

      return;
    }

    addToCart(
      activeVariant.referenceKey,
      state.quantity,
      gtmData?.pageType,
      product.limitForCart,
    );
  };

  return (
    <ProductSizesProvider product={product}>
      <div
        className={styles.root}
        onClick={() => !isTablet && push(product.href)}
      >
        <div
          className={clsx(styles.numberIcon, styles[`numberIcon${index + 1}`])}
        />
        <Link href={product.href} className={styles.image}>
          <Image
            src={product.coverImage}
            alt={product.name}
            className={styles.image}
          />
        </Link>
        <div className={styles.content}>
          <div className={styles.top}>
            <P className={styles.label}>{product.label}</P>
            {isTablet && (
              <DeliveryStatus status={DeliveryStatusType.AVAILABLE} />
            )}
          </div>
          <div className={styles.textContainer}>
            <P className={styles.category}>
              <Span>
                {[product.category, product.brand].filter((a) => a).join(' - ')}
              </Span>
            </P>
            <Link href={product.href} className={styles.name}>
              <ParseBBCode content={product.name} />
            </Link>
          </div>

          {isTablet && (
            <>
              <div className={styles.material}>
                <ProductMaterialInfo
                  size={product.size}
                  composedMaterials={product.composedMaterials}
                />
                {product.size && <ProductSize size={product.size as []} />}
              </div>
              {product.price?.reference && (
                <ProductReferencePrice
                  price={product.price}
                  className={styles.measurable}
                />
              )}

              <ReviewsRating
                value={parsedProduct?.ratingScore}
                count={parsedProduct?.ratingCount}
                url={product.href}
                size="small"
                className={styles.reviews}
              />
              <div className={styles.colors}>
                {sortedVariants.map((variant) => (
                  <VariantItem
                    key={variant.id}
                    colors={variant.colors}
                    isActive={variant.id.toString() === product?.id?.toString()}
                    onClick={() =>
                      setState((s) => ({
                        ...s,
                        listicleProductRefKey: variant.referenceKey,
                      }))
                    }
                    className={clsx(
                      styles.variantItem,
                      variant.isSoldOut && styles.isSoldOut,
                    )}
                  />
                ))}
              </div>
              <div className={styles.priceSection}>
                <div className={styles.selectors}>
                  {hasSizes && (
                    <SizeSelect
                      control={control}
                      options={transformedSizeOptions}
                      className={styles.selector}
                    />
                  )}
                  <ProductQuantitySelect
                    quantityLimit={quantityLimit}
                    setQuantity={(quantity) =>
                      setState((s) => ({ ...s, quantity }))
                    }
                    isOutOfStock={isDisabled}
                    className={styles.selector}
                  />
                </div>
                <div className={styles.column}>
                  <PriceInfo>
                    <ProductDiscount product={product} />
                    <ProductPrice
                      activeVariant={activeVariant}
                      className={styles.price}
                    />
                  </PriceInfo>
                  <Vat rate={product.taxRatePercent} className={styles.vat} />

                  <Button
                    className={styles.button}
                    onClick={handleClick}
                    disabled={isDisabled}
                  >
                    {t('common.buttons.buy')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        {isMobile && (
          <PriceInfo>
            <ProductDiscount product={product} />
            <ProductPrice
              activeVariant={activeVariant}
              className={styles.price}
            />
          </PriceInfo>
        )}
      </div>
    </ProductSizesProvider>
  );
};
